/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import ReferencedComponent from '../Component/ComponentReference/ReferencedComponent';
import ComponentBase from '../ComponentBase';
import StringList from '../Editor/StringList';
import TypeStrings from '../TypeStrings';
import CustomMarker from './CustomMarker';
import EditorComponentBase from './EditorComponentBase';
import NodeKey from './NodeKey';
import PrefabMarker from './PrefabMarker';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

enum ChildBehaviour
{
    None = 0,
    All = 1,
    MarkedOnly = 2,
    MarkedExcluded = 3
}
const ccChildBehaviour: object = cc.Enum(ChildBehaviour);

@cc._decorator.ccclass
@cc._decorator.executeInEditMode
@cc._decorator.disallowMultiple
export default class SmartPrefab extends EditorComponentBase
{
    public static SyncStack: SmartPrefab[] = [];

    public static readonly ChildrenPropertyName: string = '_children';

    public static readonly ExcludedProperties: string[] =
    [
        SmartPrefab.ChildrenPropertyName,
        '_id',
        '_components'
    ];

    private static readonly DefaultNodeProperties: string[] =
    [
        '_active',
        '_color',
        '_contentSize',
        '_globalZOrder',
        '_localZOrder',
        '_name',
        '_opacity',
        '_position',
        '_rotationX',
        '_rotationY',
        '_scaleX',
        '_scaleY',
        '_skewX',
        '_skewY',
    ];

    @property(cc.Prefab)
    protected prefab: cc.Prefab = null;

    @property({ type: ccChildBehaviour })
    protected childBehaviour: ChildBehaviour = ChildBehaviour.None;

    @property({ readonly: true })
    protected prefabVersion: number = 0;

    @property({ readonly: true })
    public get Version(): number { return this.prefabVersion + 1; }

    @property()
    public get SyncPrefab(): boolean { return false; }
    public set SyncPrefab(value: boolean) { this.syncPrefab(true); }

    @property()
    protected debug: boolean = false;

    @property()
    protected hasCustomNodeAttributes: boolean = false;

    @property(
    {
        type: StringList,
        readonly: true,
        visible: (function(): boolean { return this.hasCustomNodeAttributes; })
    })
    protected optionalNodeProperties: StringList = null;

    private _prefabInstance: cc.Node = null;
    private _changes: string[] = [];
    private _scene: cc.Scene = null;

    private _isFirst: boolean = false;
    private _loadIndex: number = 0;

    /** @hidden */
    protected onLoad(): void
    {
        if (!CC_EDITOR)
        {
            this.enabled = false;
            return;
        }

        if (!this.prefab)
        {
            this.error('No prefab property set.');
            return;
        }
        super.onLoad(`Prefab ${this.prefab.name}`);

        this._scene = cc.director.getScene();
        this.hasCustomNodeAttributes = this.optionalNodeProperties.HasActiveItems();

        this._loadIndex = this.node.parent.children.indexOf(this.node);
        if (SmartPrefab.SyncStack.length < 1) { this._isFirst = true; }
        else { SmartPrefab.SyncStack.push(this); }
    }

    /** @hidden */
    protected start(): void
    {
        if (this._isFirst) { this.syncSerially(); }
    }

    /** @hidden */
    protected resetInEditor(): void
    {
        this.optionalNodeProperties = new StringList(SmartPrefab.DefaultNodeProperties.slice(0));
    }

    private syncSerially(): void
    {
        this.syncPrefab();
        this.runNext();
    }

    private syncPrefab(force: boolean = false): void
    {
        const parent: cc.Node = this.node.parent;
        if (!parent) { return; }

        // Store index, to preserve ordering after children re-added.
        let index: number = parent.children.indexOf(this.node);
        if (index < 0) { index = this._loadIndex; }

        // Check if editing in prefab.
        if (this._scene === parent)
        {
            this.prefabVersion++;

            this.updateNodeKeysRecusrively(this.node);
            this.warn(`Editing prefab version [${this.prefabVersion}].`);
            return;
        }

        this.log('Syncing prefab...');

        // Instantiate fresh prefab object
        const smartPrefab: SmartPrefab = this.instantiateFromPrefab(force);
        if (!smartPrefab) { return; }

        this.log(`Needs update - CURRENT: [${this.prefabVersion}], PREFAB: [${smartPrefab.prefabVersion}].`);

        this._changes.length = 0;
        this.reflectPrefabRecursively(this._prefabInstance, this.node, true);

        // Hack: position and such serialized fields not respected / refreshed by editor.
        // new instantiation fixes this.
        this._prefabInstance = cc.instantiate(this._prefabInstance);

        if (this.debug && this._changes.length < 1)
        {
            this.log('No changes detected.');
        }

        const siblings: cc.Node[] = parent.children.slice(0);
        siblings[index] = this._prefabInstance;
        this.log(`Updated in scene - PARENT: [ ${parent.name} ], INDEX: [ ${index} ].`);

        if (index < 0 || siblings.some(child => !child))
        {
            this.error('Child index out of range.');
            return;
        }

        // Hack: child insert does not refresh editor.
        parent.removeAllChildren();
        this.scheduleOnce(() =>
        {
            siblings.forEach(child => parent.addChild(child));
        });

        // Add a copy of original node for debug.
        if (this.debug)
        {
            SmartPrefab.DeactivateReferencedComponents(this.node);

            this.node.name = `Removed [${this.node.name}] - ${Date.now()}`;
            this.node.active = false;
            this.prefab = null;

            const refreshedNode: cc.Node = cc.instantiate(this.node);
            this._scene.addChild(refreshedNode);
        }

        // Cleanup
        this.node.destroy();
    }

    private runNext(): void
    {
        const next: SmartPrefab = SmartPrefab.SyncStack.pop();
        if (next) { next.syncSerially(); }
    }

    private instantiateFromPrefab(force = false): SmartPrefab
    {
        if (!this.prefab)
        {
            this.error('No prefab set.');
            return null;
        }

        // Lets not have any sitting around in memory..
        if (this._prefabInstance)
        {
            this._prefabInstance.destroy();
            this._prefabInstance = null;
        }

        const newInstance: cc.Node = cc.instantiate(this.prefab);
        if (!newInstance) { return null; }

        // Check for last version compared to this instance last update.
        const smartPrefab: SmartPrefab = newInstance.getComponent(SmartPrefab);
        if (!smartPrefab)
        {
            this.error(`No SmartPrefab component attached to prefab.`);
            return null;
        }

        this.copyKey(this.node, this._prefabInstance);

        this.optionalNodeProperties.SyncActiveItems(smartPrefab.optionalNodeProperties);

        // Up to date.
        if (smartPrefab.Version === this.prefabVersion && !force)
        {
            this.log(`Up to date - PREFAB VERSION: [${smartPrefab.Version}].`);
            return null;
        }

        // Old versioned prefab detected, log and bail.
        if (smartPrefab.Version < this.prefabVersion && !force)
        {
            this.error(`Version is less than current. Manual sync required.`);
            return null;
        }

        smartPrefab.prefabVersion = smartPrefab.Version;
        this._prefabInstance = newInstance;
        return smartPrefab;
    }

    // #region Recurse Helpers

    private updateNodeReferenceRecursively(node: cc.Node, original: cc.Node, replacement: cc.Node): void
    {
        const components: cc.Component[] = node.getComponents(cc.Component);
        components.forEach(component =>
            {
                SmartPrefab.UpdateNodeReferenceOnObject(component, original, replacement);
            });

        node.children.forEach(child =>
            {
                if (child === original || child === replacement) { return; }
                this.updateNodeReferenceRecursively(child, original, replacement);
            }, this);
    }

    private updateNodeKeysRecusrively(node: cc.Node): void
    {
        const custom: CustomMarker = node.getComponent(CustomMarker);
        if (custom) { return; }

        ComponentBase.GetOrAddComponent(node, NodeKey);
        node.children.forEach(child => this.updateNodeKeysRecusrively(child));
    }

    // tslint:disable:no-string-literal
    private reflectPrefabRecursively(prefabNode: cc.Node, source: cc.Node, top: boolean = false): void
    {
        switch (this.childBehaviour)
        {
            case ChildBehaviour.None:
            {
                this.reflectNode(prefabNode, source, false);
                ComponentBase.ReplaceChildren(prefabNode, source);
                break;
            }
            case ChildBehaviour.All:
                if (top) { this.addCustomChildNodes(prefabNode, source); }
                this.reflectNode(prefabNode, source);
                this.reflectChildren(prefabNode.children, source);
                break;
            case ChildBehaviour.MarkedOnly:
            {
                let markers: PrefabMarker[] = prefabNode.getComponentsInChildren(PrefabMarker);
                markers.forEach(marker =>
                    {
                        // Ignore sub-prefabs.
                        let smartParent: SmartPrefab = ComponentBase.GetFirstParent(marker.node, SmartPrefab);
                        if (smartParent && smartParent.node !== this._prefabInstance) { return; }

                        let match: cc.Node = this.findMatchingNode(marker.node, source);
                        if (match)
                        {
                            this.reflectNode(match, marker.node, false);
                        }
                        else
                        {
                            this.log(`No matching node found: [${marker.node.name}].`);
                        }
                    });
                ComponentBase.ReplaceChildren(prefabNode, source);
                break;
            }
            case ChildBehaviour.MarkedExcluded:
            {
                if (!prefabNode.getComponent(PrefabMarker))
                {
                    this.reflectNode(prefabNode, source);
                }
                break;
            }
        }
    }
    // tslint:enable:no-string-literal

    private reflectNode(target: cc.Node, source: cc.Node, updateReference: boolean = true): void
    {
        if (source.getComponent(PrefabMarker)) { ComponentBase.GetOrAddComponent(target, PrefabMarker); }

        this.reflectNodeProperties(target, source);
        this.reflectNodeComponents(target, source);

        if (!updateReference) { return; }
        this.updateNodeReferenceRecursively(this._scene, source, target);
    }

    private reflectChildren(children: cc.Node[], source: cc.Node): void
    {
        for (let child of children)
        {
            // Ignore sub-prefabs.
            if (child.getComponent(SmartPrefab)) { continue; }

            const sourceMatchChild: cc.Node = this.findMatchingNode(child, source);
            if (!sourceMatchChild)
            {
                this.warn(`No child node matching [${child.name} - ${child.tag}].`);
                return;
            }
            this.reflectPrefabRecursively(child, sourceMatchChild);
        }
    }

    private findMatchingNode(findTarget: cc.Node, parentOther: cc.Node): cc.Node
    {
        if (NodeKey.HasSameKey(findTarget, parentOther)) { return parentOther; }

        const match: cc.Node = this.findNodeByKeyRecursively(parentOther,
            ComponentBase.GetOrAddComponent(findTarget, NodeKey).Key);

        return match || this.findByOtherNodeRecusrively(parentOther, findTarget);
    }

    private findNodeByKeyRecursively(node: cc.Node, key: string): cc.Node
    {
        const nodeKey: NodeKey = node.getComponent(NodeKey);
        if (nodeKey)
        {
            if (nodeKey.Key === key) { return node; }
        }

        for (let child of node.children)
        {
            const match: cc.Node = this.findNodeByKeyRecursively(child, key);
            if (match) { return match; }
        }

        return null;
    }

    private findByOtherNodeRecusrively(node: cc.Node, other: cc.Node): cc.Node
    {
        if (node.name === other.name)
        {
            const nodePath: string = ComponentBase.GetIndexedNodePath(node, SmartPrefab);
            const otherNodePath: string = ComponentBase.GetIndexedNodePath(other, SmartPrefab);
            if (nodePath === otherNodePath) { return node; }

            this.log(`NODE PATHS: [${nodePath}] !== [${otherNodePath}].`);
        }

        for (let child of node.children)
        {
            const match: cc.Node = this.findByOtherNodeRecusrively(child, other);
            if (match) { return match; }
        }

        return null;
    }

    private copyKey(target: cc.Node, source: cc.Node): void
    {
        // Sync Node Key Component
        const sourceKey: NodeKey = source ? source.getComponent(NodeKey) : null;
        if (sourceKey)
        {
            ComponentBase.GetOrAddComponent(target, NodeKey).Key = sourceKey.Key;
        }
        else
        {
            ComponentBase.GetOrAddComponent(target, NodeKey);
        }
    }

    private addCustomChildNodes(target: cc.Node, source: cc.Node): void
    {
        // Get unique top level custom nodes.
        const customNodes: cc.Node[]
            = ComponentBase.FilterChildren(source.getComponentsInChildren(CustomMarker).map(x => x.node));
        this.log(`Custom nodes: [${customNodes.map(node => node.name).join(', ')}]`);
        customNodes.forEach(customNode =>
            {
                // Find the right parent.
                let matchParent: cc.Node = this.findMatchingNode(customNode.parent, target);

                // Remove current parent.
                customNode.removeFromParent();

                // Uh oh, set to root..
                if (!matchParent)
                {
                    this.error(`Could not find matching parent for node [${customNode.name}].`);
                    matchParent = target;
                }

                if (customNode.parent === matchParent) { return; }

                matchParent.addChild(customNode);
            }, this);
    }

    private reflectNodeProperties(target: cc.Node, source: cc.Node): void
    {
        if (!target || !source) { return; }

        this.copyKey(target, source);

        // Fetch active optional props.
        const optionalNodeProps: string[] = this.optionalNodeProperties.GetActiveItems().map(x => x.Value);

        if (this.debug)
        {
            this.log(`OPTIONAL PROPS: [${JSON.stringify(optionalNodeProps)}].`);
        }

        // Reflecting source properties, removed properties shouldn't update.
        Object.keys(target).forEach(key =>
            {
                // Skip for now, will use cocos node and component methods.
                if (SmartPrefab.ExcludedProperties.some(prop => prop === key)) { return; }
                // Do not restore, unless optional and active.
                if (!optionalNodeProps.find(prop => prop === key)) { return; }

                // Set values from original.
                if (target[key] !== source[key])
                {
                    target[key] = source[key];
                    this._changes.push(key);
                    if (this.debug) { this.log(`Set custom node prop [${key}].`); }
                }
            }, this);
    }

    private reflectNodeComponents(target: cc.Node, source: cc.Node): void
    {
        // Update property values from prefab object
        const targetComponents: cc.Component[] = target.getComponents(cc.Component);
        const sourceComponents: cc.Component[] = source.getComponents(cc.Component);

        targetComponents.forEach(targetComponent =>
            {
                const sourceComponent: cc.Component
                        = sourceComponents.find(srcComp => srcComp.name === targetComponent.name);
                // No matching component to sync properties.
                if (!sourceComponent)
                {
                    this.log(`No matching component found: [${targetComponent.name}]`);
                    return;
                }

                this.reflectSingleComponent(targetComponent, sourceComponent);
            }, this);
    }

    private reflectSingleComponent(targetComponent: object, sourceComponent: object): void
    {
        if (!targetComponent) { return; }
        const targetCustomProperties: string[] = ComponentBase.GetCustomProperties(targetComponent);

        const sourceKeys: string[] = Object.keys(sourceComponent);
        const targetKeys: string[] = Object.keys(targetComponent);

        // Properties that don't exist in new prefab will be lost.
        const removed: string[] = sourceKeys.filter(key => !targetKeys.find(x => x === key));
        removed.forEach(remove => this._changes.push(`-${remove}`));

        targetKeys.forEach(key =>
            {
                // Recurse down object properties.
                if (ComponentBase.HasCustomProperties(targetComponent[key]))
                {
                    this.reflectSingleComponent(targetComponent[key], sourceComponent[key]);
                    return;
                }

                // Bail if not custom.
                if (!targetCustomProperties || !targetCustomProperties.some(prop => prop === key)) { return; }

                // Set custom prop on prefab from scene.
                if (targetComponent[key] !== sourceComponent[key])
                {
                    targetComponent[key] = sourceComponent[key];
                    this._changes.push(key);
                    if (this.debug) { this.log(`Custom prop preserved: [${key}].`); }
                }
            }, this);
    }

    // #endregion

    // #region Static Helpers

    // Using max depth here to prevent stack overflow for deeply nested properties
    // that most likely wouldn't be exposed to editor.
    // Deeply nested children and components are all reached, only for properties.
    private static readonly _maxDepth: number = 10;
    public static UpdateNodeReferenceOnObject(
        obj: any,
        original: any,
        replacement: any,
        depth: number = this._maxDepth): void
    {
        if (!obj || typeof(obj) !== TypeStrings.object || depth < 1) { return; }

        if (Array.isArray(obj))
        {
            (obj as any[]).forEach((value, index, arr) =>
                {
                    if (value === original)
                    {
                        arr[index] = replacement;
                    }
                }, this);
            return;
        }

        Object.keys(obj).forEach(key =>
            {
                const prop: any = obj[key];
                if (typeof(prop) === TypeStrings.object)
                {
                    if (!prop) { return; }
                    if (prop === original) { obj[key] = replacement; }
                    else { this.UpdateNodeReferenceOnObject(prop, original, replacement, --depth); }
                }
            }, this);
    }

    public static DeactivateReferencedComponents(node: cc.Node): void
    {
        // Set is active on [[ReferencedComponent]] to false, prevent polluting injection.
        const referencedComponents: ReferencedComponent[] = node.getComponents(ReferencedComponent) || [];
        // Let not have any children mucking it up..
        referencedComponents.push(...node.getComponentsInChildren(ReferencedComponent));
        referencedComponents.forEach(comp => comp.IsActive = false);
    }

    // #endregion
}
